/**
 * The app's real memory footprint, from the browser's own accounting (performance.
 * measureUserAgentSpecificMemory: Chromium only, and only in a cross-origin-isolated page, which
 * public/_headers makes this one). Reported as a `memory` client event once shortly after launch and
 * then every MEMORY_INTERVAL_MS while the page is visible, with the total and a breakdown by what the
 * browser attributes the bytes to (JavaScript, DOM, Canvas, Shared...), so the workshop's device
 * log answers "how big is it on this phone" with a number instead of an estimate. Safari has no
 * equivalent API; there the function simply never reports.
 */
export const MEMORY_FIRST_MS = 15_000;
export const MEMORY_INTERVAL_MS = 10 * 60_000;

interface MemoryBreakdown {
  bytes: number;
  types: string[];
}
export interface MemoryMeasurement {
  bytes: number;
  breakdown: MemoryBreakdown[];
}

/** Total plus bytes per type label ("JavaScript", "DOM", "Canvas", "Shared", or a joined pair), in KB. */
export function summarizeMemory(m: MemoryMeasurement): { totalKb: number; byType: Record<string, number> } {
  const byType: Record<string, number> = {};
  for (const part of m.breakdown) {
    const key = part.types.length ? part.types.join("+") : "Other";
    byType[key] = (byType[key] ?? 0) + Math.round(part.bytes / 1024);
  }
  return { totalKb: Math.round(m.bytes / 1024), byType };
}

type MeasuringPerformance = Performance & { measureUserAgentSpecificMemory?: () => Promise<MemoryMeasurement> };

/** Whether this page can measure at all: the API exists and the page is cross-origin isolated. */
export function canMeasureMemory(): boolean {
  const perf = performance as MeasuringPerformance;
  return typeof perf.measureUserAgentSpecificMemory === "function" && (globalThis as { crossOriginIsolated?: boolean }).crossOriginIsolated === true;
}

export function startMemoryReports(): () => void {
  if (!canMeasureMemory()) return () => {};
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  const measure = async () => {
    timer = null;
    if (stopped) return;
    if (document.visibilityState === "visible") {
      try {
        const m = await (performance as MeasuringPerformance).measureUserAgentSpecificMemory!();
        // Imported here, not at the top: telemetry reads browser globals when it loads, and the
        // pure summary above is unit-tested in Node.
        const { report } = await import("./telemetry");
        report("memory", "", summarizeMemory(m));
      } catch {
        /* the browser may refuse (not isolated after all, or measuring too often); try again later */
      }
    }
    if (!stopped) timer = setTimeout(measure, MEMORY_INTERVAL_MS);
  };
  timer = setTimeout(measure, MEMORY_FIRST_MS);
  return () => {
    stopped = true;
    if (timer !== null) clearTimeout(timer);
  };
}
